import { useMemo, useRef, useState } from 'react'
import { useApiClient } from '../../hooks/useApiClient';
import { ReactSelectOption } from '../../types';
import { Col, Card, Form, Nav, Row, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { VehicleEntity } from '../../api';
import MotornetVehicleSelector, { MakeOption, MotornetSelectedVehicle, VersionOption, getMakeName } from '../../motornet/common/MotornetVehicleSelector';
import { productionFetchParams } from '../../motornet/common/selectorMeta';
import AsyncSelect from 'react-select/async';
import i18next from 'i18next';
import SelectOptWithSubtitle from '../SelectOptWithSubtitle/SelectOptWithSubtitle';
import _ from 'lodash';
import { createVehicleSearch } from 'src/vehicleList/vehicleSearch';
import useLeadsStore, { useLeadSelectedVehicle } from '../../store/LeadsStore';
import VehicleCardBody from '../../components/VehicleCardBody/VehicleCardBody';
import { LeadsWizardActiveStepEnum, RoleEnum } from '../../api/models/Enums';

import { FaArrowLeftLong, FaArrowRightLong } from 'react-icons/fa6';
import { useAuth } from 'src/hooks/useAuth';

const SelectHmpOrExternalVehicle = () => {
  const storeSelectedVehicle = useLeadSelectedVehicle();
  const [selectedVehicle, setSelectedVehicle] = useState<MotornetSelectedVehicle>({
    brand: storeSelectedVehicle?.brand ?? null,
    model: storeSelectedVehicle?.model ?? null,
    version: storeSelectedVehicle?.version ?? null,
  })
  const [images, setImages] = useState<string[]>(storeSelectedVehicle?.images ?? [])
  const { t } = useTranslation()
  const { apiClient, handleApiResponse } = useApiClient();
  const [vehicleSearchOpt, setVehicleSearchOpt] = useState<ReactSelectOption | null>(null)
  const [isPressedExternal, setIsPressedExternal] = useState(false)
  const { setLeadWizardData, leadWizardData, setWizardActiveStep, vehicleSource, setVehicleSource } = useLeadsStore();
  const { userRole } = useAuth();

  const modelLabel = (model: any) => model?.data?.gammaModello?.descrizione || model?.label || '';

  const selectionRef = useRef<MotornetSelectedVehicle>(selectedVehicle);
  const updateSelection = (next: MotornetSelectedVehicle) => {
    selectionRef.current = next;
    setSelectedVehicle(next);
  };

  // Persist the current selection + custom vehicle (brand/model strings) into the wizard.
  const commitCustomVehicle = (next: MotornetSelectedVehicle, imgs: string[]) => {
    if (!next.brand) {
      setLeadWizardData({ vehicle: undefined, motornetSelectedVehicle: undefined });
      return;
    }
    setLeadWizardData({
      motornetSelectedVehicle: { ...next, images: imgs },
      vehicle: {
        customVehicle: {
          brand: getMakeName(next.brand),
          model: modelLabel(next.model),
        },
        vehicleId: null,
      },
    });
  };

  const canSendQuote =
    !!leadWizardData?.vehicle &&
    (vehicleSource !== 'external' || !!leadWizardData.vehicle.customVehicle?.model);

  const fetchImages = async (next: MotornetSelectedVehicle) => {
    const eurotax = (next.version as VersionOption | null)?.data?.codiceEurotax;
    if (!eurotax) {
      setImages([]);
      commitCustomVehicle(next, []);
      return;
    }
    try {
      const vehicleKind = (next.brand as MakeOption | null)?.data?.vehicleKind ?? 'auto';
      const payload = await apiClient.motornetControllerService.getMotornetVehicle(eurotax, vehicleKind, productionFetchParams((next.version as VersionOption | null)?.data, next.model?.data));
      const imgs = (payload?.images ?? []).map((img: any) => img?.url).filter(Boolean);
      setImages(imgs);
      commitCustomVehicle(next, imgs);
    } catch (e) {
      console.warn(e);
      setImages([]);
      commitCustomVehicle(next, []);
    }
  };


  const setVehicleSearch = (opt: any) => {
    setVehicleSearchOpt(opt)
    if (!opt) return;

    // FETCH THE VEHICLE SELECTED FROM THE DROPDOWN
    handleApiResponse(apiClient.vehicleController.getVehicles1(opt.vehicle.id))
      .then((res: VehicleEntity) => {
        setLeadWizardData({
          motornetSelectedVehicle: undefined,
          vehicle: {
            customVehicle: null,
            fullVehicleData: res
          }
        });
      })
      .catch(error => {
        console.error('Error fetching vehicle details: ', error);
      });
  }

  const vehicleSearch = useMemo(
    () => createVehicleSearch((params) => apiClient.motornetControllerService.getPlatformVehicles(params)),
    [apiClient]
  );

  return (
    <div>
      <Row className='mb-3'>
        <Nav
          variant='tabs'
          activeKey={vehicleSource}
          onSelect={(key) => {
            setVehicleSource(key || 'hmp');
            if (!!leadWizardData.vehicle) {
              setLeadWizardData({ vehicle: undefined, motornetSelectedVehicle: undefined })
            }
            updateSelection({ brand: null, model: null, version: null })
            setVehicleSearchOpt(null)
            setImages([])
            setIsPressedExternal(false)
          }}>
          <Nav.Item>
            <Nav.Link eventKey="hmp">	{t('vehicle_hmp')}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="external">	{t('vehicle_external')}</Nav.Link>
          </Nav.Item>
        </Nav>
      </Row>

      {/* SELECT HMP VEHICLE */}
      <Row>
        {vehicleSource === 'hmp' && (
          <Col md={6}>
            <Form.Group>
              <AsyncSelect
                menuPosition="fixed"
                placeholder={i18next.t('select')}
                value={vehicleSearchOpt}
                noOptionsMessage={() => i18next.t('vehicle_search_placeholder', { defaultValue: 'Ordine, telaio, targa o codice costruttore' })}
                loadOptions={vehicleSearch}
                onChange={setVehicleSearch}
                components={{
                  Option: (props: any) => <SelectOptWithSubtitle {...props} />
                }}
                isClearable
              />

            </Form.Group>
          </Col>
        )}

        {/* SELECT EXTERNAL VEHICLE */}
        {vehicleSource === 'external' && (
          <MotornetVehicleSelector
            selectedVehicle={selectedVehicle}
            onBrandChange={(brand) => {
              setIsPressedExternal(true)
              const next = { brand, model: null, version: null }
              updateSelection(next)
              setImages([])
              commitCustomVehicle(next, [])
            }}
            onModelChange={(model) => {
              const next = { ...selectionRef.current, model, version: null }
              updateSelection(next)
              setImages([])
              commitCustomVehicle(next, [])
            }}
            onVersionChange={(version) => {
              const next = { ...selectionRef.current, version }
              updateSelection(next)
              fetchImages(next)
            }}
          />
        )}

        <Card className='mt-4'>
          {(leadWizardData?.vehicle && !isPressedExternal && leadWizardData?.vehicle?.fullVehicleData) ? (
            <VehicleCardBody data={leadWizardData?.vehicle?.fullVehicleData} />
          ) :

            (!_.isEmpty(leadWizardData?.vehicle?.customVehicle) && (
              <VehicleCardBody
                data={{
                  dati_veicolo: {
                    marca: leadWizardData?.vehicle?.customVehicle?.brand,
                    modello: leadWizardData?.vehicle?.customVehicle?.model
                  },
                  image_list: images.map(el => {
                    return { url: el }
                  })
                }}
              />
            ))}
        </Card>

        <Col className="d-flex justify-content-between align-items-center">
          <Button
            onClick={() => setWizardActiveStep(LeadsWizardActiveStepEnum.OPPORTUNITY)}
            style={{ marginTop: '10px', marginRight: '10px' }}
          >
            <FaArrowLeftLong className="me-2" />
            {t('opportunity')}
          </Button>

          <Button
            disabled={!canSendQuote}
            onClick={() => setWizardActiveStep(LeadsWizardActiveStepEnum.SEND_QUOTE)}
            style={{ marginTop: '10px' }}
          >
            {userRole === RoleEnum.SALES_DEALER ? i18next.t('send_upload_quote') : i18next.t('send_quote')}
            <FaArrowRightLong className="ms-2" />
          </Button>
        </Col>
      </Row>
    </div>


  )
}

export default SelectHmpOrExternalVehicle
